'use client'

import { cn } from '@morpho-org/ui-v6/utils'
import { cva } from 'class-variance-authority'
import { forwardRef } from 'react'

import { Avatar, type AvatarProps, type AvatarSize } from './Avatar'

const rightAvatarVariants = cva(
  // Base styles - shift left to overlap with left avatar
  '',
  {
    variants: {
      size: {
        xsmall: '-ml-[7px]',
        small: '-ml-sm',
        medium: '-ml-[14px]',
        regular: '-ml-[21px]',
        big: '-ml-[30px]',
        large: '-ml-[47px]'
      }
    },
    defaultVariants: {
      size: 'small'
    }
  }
)

export interface AvatarPairProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  leftAvatarProps: AvatarProps
  rightAvatarProps: AvatarProps
  size?: AvatarSize
}

/**
 * AvatarPair component displays two overlapping avatars.
 * Commonly used for showing token pairs in DeFi contexts.
 *
 * @example
 * ```tsx
 * <AvatarPair
 *   leftAvatarProps={{ src: "/eth.png" }}
 *   rightAvatarProps={{ src: "/usdc.png" }}
 *   size="medium"
 * />
 * ```
 */
export const AvatarPair = forwardRef<HTMLDivElement, AvatarPairProps>(
  ({ leftAvatarProps, rightAvatarProps, size = 'small', className }, ref) => {
    const isNumericSize = typeof size === 'number'

    // For numeric sizes, calculate margin-left as approximately half the size + padding
    const numericMarginLeft = isNumericSize ? -(size / 2 + 2) : undefined

    return (
      <div ref={ref} className={cn('inline-flex items-center', className)}>
        <Avatar {...leftAvatarProps} size={size} />
        <Avatar
          {...rightAvatarProps}
          size={size}
          className={cn(
            !isNumericSize && rightAvatarVariants({ size }),
            rightAvatarProps.className
          )}
          style={
            isNumericSize
              ? { marginLeft: numericMarginLeft, ...rightAvatarProps.style }
              : rightAvatarProps.style
          }
        />
      </div>
    )
  }
)

AvatarPair.displayName = 'AvatarPair'
